// src/Lexbuild/AgentSync.js
// Utility module for bidirectional sync between workflow graph and form schema via LLM

/**
 * callOpenAI
 * A thin browser-safe wrapper around OpenAI’s ChatCompletion endpoint.
 * @param {string} prompt
 * @returns {Promise<string>} model’s text reply
 */
async function callOpenAI(prompt) {
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: [{ role: "system", content: prompt }],
        temperature: 0.0,
      }),
    });
    if (!res.ok) throw new Error(`OpenAI error: ${res.statusText}`);
    const { choices } = await res.json();
    return choices[0].message.content;
  }
  
  /**
   * Generate a JSON Schema for form inputs based on the current workflow graph.
   * @param {object} graphJson – ReactFlow graph JSON (nodes & edges)
   * @returns {object} schemaJson
   */
  export async function generateSchemaFromWorkflow(graphJson) {
    const prompt = `
  You are a form-schema generator. Given this workflow JSON, collect all node.data.fields and example values, infer types, and emit a merged JSON Schema with properties and required array. Respond with valid JSON only.
  
  Workflow JSON:
  ${JSON.stringify(graphJson, null, 2)}
  `;
    const reply = await callOpenAI(prompt);
    return JSON.parse(reply);
  }
  
  /**
   * Generate a minimal workflow graph JSON based on a JSON Schema.
   * @param {object} schemaJson
   * @returns {object} graphJson
   */
  export async function generateWorkflowFromSchema(schemaJson) {
    const prompt = `
  You are a workflow generator. Given this JSON Schema of inputs, produce a minimal ReactFlow graph JSON:
  1. A form-input node for each property.
  2. Connect url -> httpRequest, selector -> extractHtml, etc.
  3. End in outputResult. Respond with JSON only.
  
  Schema JSON:
  ${JSON.stringify(schemaJson, null, 2)}
  `;
    const reply = await callOpenAI(prompt);
    return JSON.parse(reply);
  }
  
  /**
   * Compare graph fields vs. schema properties, return mismatch warnings.
   * @param {object} graphJson
   * @param {object} schemaJson
   * @returns {Array<{type:string, message:string}>}
   */
  export function detectMismatches(graphJson, schemaJson) {
    const graphFields = new Set();
    graphJson.nodes.forEach(n => {
      if (n.data?.fields) {
        Object.keys(n.data.fields).forEach(f => graphFields.add(f));
      }
    });
  
    const schemaFields = new Set(Object.keys(schemaJson.properties || {}));
    const warnings = [];
  
    // Fields declared in schema but not in any node
    for (let f of schemaFields) {
      if (!graphFields.has(f)) {
        warnings.push({ type: 'schema', message: `Form field '${f}' not found in any node.` });
      }
    }
    // Fields present in nodes but not in schema
    for (let f of graphFields) {
      if (!schemaFields.has(f)) {
        warnings.push({ type: 'graph', message: `Node field '${f}' is not declared in the form.` });
      }
    }
    return warnings;
  }
  